'use client';

import { useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';

import { LoginForm } from '@/components/auth/LoginForm';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/lib/constants';
import { ThemeAwareLogo } from '@/components/ui/ThemeAwareLogo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function LandingLogin() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAuthenticated } = useAuth();
  const redirectTo = searchParams.get('from') || ROUTES.DASHBOARD;

  useEffect(() => {
    if (isAuthenticated) {
      router.replace(redirectTo);
    }
  }, [isAuthenticated, router, redirectTo]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto items-center min-h-[calc(100vh-4rem)]">
          {/* Left Side - Hero Content */}
          <div className="space-y-8">
            <div className="space-y-5">
              <div className="flex items-center space-x-3">
                <ThemeAwareLogo width={50} height={50} />
                <div className="text-sm font-medium dark:text-gray-400 tracking-wide">FOMOGO</div>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white leading-tight tracking-tight">
                Hire Fast, Smart with{' '}
                <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                  AI
                </span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                Transform your hiring process with intelligent automation and data-driven insights.
              </p>
            </div>

            {/* Video Demo Card */}
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-xl shadow-gray-200/50 dark:shadow-gray-900/50 overflow-hidden">
              <CardContent className="p-0">
                <div className="relative bg-gradient-to-br from-red-400 via-orange-400 to-yellow-400 aspect-video">
                  {/* YouTube iframe - muted for low volume start */}
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/OYTwV_GGbWc?mute=1&rel=0&modestbranding=1"
                    title="Fomogo Demo Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full max-w-md mx-auto">
            <Card className="card-base">
              <CardHeader className="space-y-4 pb-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
                    Welcome Back
                  </CardTitle>
                </div>
                <CardDescription className="text-gray-600 dark:text-gray-400 leading-relaxed text-base">
                  Sign in to your account to continue managing your hiring process
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0 pb-8">
                <LoginForm redirectTo={redirectTo} />

                <div className="mt-8 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Don&apos;t have an account?{' '}
                    <Link
                      href="/signup"
                      className="text-gray-900 dark:text-white hover:underline font-medium"
                    >
                      Create one here
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden mt-12">
          <div className="space-y-6">
            <div className="text-center space-y-3">
              <div className="flex justify-center items-center space-x-3">
                <ThemeAwareLogo width={32} height={32} />
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 tracking-wide">
                  Fomogo
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                Hire Fast, Smart with{' '}
                <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                  AI
                </span>
              </h1>
            </div>

            {/* Mobile Video Card */}
            <Card className="bg-white bg-background">
              <CardContent className="p-0">
                <div className="relative bg-gradient-to-br from-red-400 via-orange-400 to-yellow-400 aspect-video">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/OYTwV_GGbWc?mute=1&rel=0&modestbranding=1"
                    title="Fomogo Demo Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
